import React, { useState } from 'react'
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Alert,
  Container,
  Paper,
  InputAdornment,
  IconButton,
} from '@mui/material'
import {
  Visibility,
  VisibilityOff,
  Business,
  Person,
  Lock,
} from '@mui/icons-material'
import { useForm } from 'react-hook-form'
import { toast } from 'react-toastify'

import { useAuth } from '../../contexts/AuthContext'
import LoadingSpinner from '../../components/Common/LoadingSpinner'

interface LoginFormData {
  employee_id: string
  password: string
}

const LoginPage: React.FC = () => {
  const { login } = useAuth()
  const [loading, setLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    defaultValues: {
      employee_id: '',
      password: '',
    },
  })

  const onSubmit = async (data: LoginFormData) => {
    setLoading(true)
    setError(null)

    try {
      await login(data.employee_id, data.password)
      toast.success('ログインしました')
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'ログインに失敗しました'
      setError(errorMessage)
      toast.error(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword)
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={24}
          sx={{
            borderRadius: 3,
            overflow: 'hidden',
          }}
        >
          {/* ヘッダー部分 */}
          <Box
            sx={{
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              p: 4,
              textAlign: 'center',
            }}
          >
            <Business sx={{ fontSize: 48, mb: 2 }} />
            <Typography variant="h4" component="h1" fontWeight="bold">
              TGS社内管理システム
            </Typography>
          </Box>

          {/* ログインフォーム */}
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h5" component="h2" gutterBottom align="center" fontWeight="600">
              ログイン
            </Typography>
            
            <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
              社員IDとパスワードを入力してください
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
              <TextField
                {...register('employee_id', {
                  required: '社員IDを入力してください',
                })}
                margin="normal"
                fullWidth
                label="社員ID"
                placeholder="例: J170218"
                autoComplete="username"
                autoFocus
                error={!!errors.employee_id}
                helperText={errors.employee_id?.message}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Person color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 2 }}
              />

              <TextField
                {...register('password', {
                  required: 'パスワードを入力してください',
                })}
                margin="normal"
                fullWidth
                label="パスワード"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                error={!!errors.password}
                helperText={errors.password?.message}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={handleTogglePasswordVisibility}
                        edge="end"
                        aria-label="パスワードの表示/非表示"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 3 }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={loading}
                sx={{
                  py: 1.5,
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  mb: 2,
                }}
              >
                {loading ? <LoadingSpinner size={24} message="" /> : 'ログイン'}
              </Button>
            </Box>

            {/* パスワードについて */}
            <Box sx={{ mt: 4, p: 2, bgcolor: 'info.light', borderRadius: 1, border: '1px solid', borderColor: 'info.main' }}>
              <Typography variant="subtitle2" gutterBottom color="info.dark" fontWeight="bold">
                📌 初回ログインの方へ
              </Typography>
              <Typography variant="body2" color="info.dark">
                初期パスワードは<strong>社員IDと同じ</strong>です。<br />
                ログイン後、必ずパスワードを変更してください。
              </Typography>
            </Box>

            {/* パスワードを忘れた場合 */}
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom color="text.secondary" fontWeight="bold">
                パスワードを忘れた場合
              </Typography>
              <Typography variant="body2" color="text.secondary">
                業務推進支援部門（人事グループ）にご連絡ください。<br />
                パスワードをリセットいたします。
              </Typography>
            </Box>
          </CardContent>
        </Paper>

        {/* フッター */}
        <Typography
          variant="body2"
          color="white"
          align="center"
          sx={{ mt: 4, opacity: 0.8 }}
        >
          © {new Date().getFullYear()} トランスコスモスグローバルシステムサービス株式会社. All rights reserved.
        </Typography>
      </Container>
    </Box>
  )
}

export default LoginPage